// FE-SCRM-056 — React Query hooks for the sales-playbook surface.
//
// Apply mutation's ``onSuccess`` invalidates the opportunity's
// activities query so the spawned rows show up in the timeline +
// ActivityRail without a refresh — matches FE-SCRM-024's
// invalidation pattern.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  ACTIVITIES_QUERY_KEY,
  OPPORTUNITY_DETAIL_QUERY_KEY,
} from "../OpportunityDetail/hooks";

import { applyPlaybook, fetchPlaybookDetail, fetchPlaybooks } from "./api";
import type {
  ApplyPlaybookResponse,
  PlaybookDetail,
  PlaybookListResponse,
} from "./types";


export const PLAYBOOKS_QUERY_KEY = "sales-playbooks" as const;
export const PLAYBOOK_DETAIL_QUERY_KEY = "sales-playbook-detail" as const;


export function usePlaybooksQuery(): UseQueryResult<PlaybookListResponse> {
  return useQuery({
    queryKey: [PLAYBOOKS_QUERY_KEY],
    queryFn: ({ signal }) => fetchPlaybooks({ signal }),
  });
}


export function usePlaybookDetailQuery(
  playbookId: string | null | undefined,
): UseQueryResult<PlaybookDetail> {
  return useQuery({
    queryKey: [PLAYBOOK_DETAIL_QUERY_KEY, playbookId],
    queryFn: ({ signal }) => fetchPlaybookDetail(playbookId!, { signal }),
    enabled: Boolean(playbookId),
  });
}


export interface ApplyPlaybookVariables {
  playbook_id: string;
}


export function useApplyPlaybookMutation(
  opportunityId: string,
): UseMutationResult<ApplyPlaybookResponse, Error, ApplyPlaybookVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) => applyPlaybook(opportunityId, vars.playbook_id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [ACTIVITIES_QUERY_KEY, opportunityId],
      });
      qc.invalidateQueries({
        queryKey: [OPPORTUNITY_DETAIL_QUERY_KEY, opportunityId],
      });
    },
  });
}


// FE-PINV-074 — applies the playbook against an opportunity_id known
// only at call time (pick mode + bulk fan-out). Returns an
// imperative ``applyForOpportunity`` so the bulk loop can ``await``
// each call sequentially and gather per-op failures. Both branches
// invalidate the per-opportunity activities + detail caches so the
// downstream rails (ActivityRail on the opportunity detail, plus
// any open list-row touchpoint surface) refetch fresh data.
export interface ApplyForOpportunityVars {
  opportunity_id: string;
  playbook_id: string;
}


export interface ApplyForOpportunityHandle {
  applyForOpportunity: (
    vars: ApplyForOpportunityVars,
  ) => Promise<ApplyPlaybookResponse>;
  isPending: boolean;
}


export function useApplyPlaybookForOpportunity(): ApplyForOpportunityHandle {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (vars: ApplyForOpportunityVars) =>
      applyPlaybook(vars.opportunity_id, vars.playbook_id),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({
        queryKey: [ACTIVITIES_QUERY_KEY, vars.opportunity_id],
      });
      qc.invalidateQueries({
        queryKey: [OPPORTUNITY_DETAIL_QUERY_KEY, vars.opportunity_id],
      });
    },
  });
  return {
    applyForOpportunity: (vars) => m.mutateAsync(vars),
    isPending: m.isPending,
  };
}
